import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { CompanyFinancial, CompanyPricing, Note } from '../../shared/models';


/**
 * Singleton class to handle events related with the company page
 */
@Injectable()
export class CompanyEventsService {
  public alertRecordsUpdated$ = new Subject<null>();
  public autoSaveDraftNotifier$ = new Subject<Note>();
  public openModal$ = new Subject<any>();
  public collapseSections$ = new Subject<null>();
  public openCompanySection$ = new Subject<{section: string, updateCounter?: boolean}>();
  public openNotesSection$ = new Subject<{section: boolean, noteId: number}>();
  public updateAlertsCounter$ = new Subject<number>();
  public updateCompanySectionCounter$ = new Subject<{section: string, value: number}>();
  public companyLinksUpdated$ = new Subject<null>();
  public companySecuritiesUpdated$ = new Subject<null>();
  public companyIdentifiersUpdated$ = new Subject<null>();
  public companySpaceUpdated$ = new Subject<null>();
  public currentCompanySpaceDeleted$ = new Subject<null>();
  public openFinancialEditModal$ = new Subject<any>();
  public spaceChanged$ = new Subject<number>();
  public companyRouteParamChanged$ = new Subject<{isFirstLoad: boolean, companyId: number}>();
  public openCopyModal$ = new Subject<{
    openMarketText: string,
    offHourText?: string,
  }>();
  public openOffHourCopyModal$ = new Subject<{
    openMarketText: string,
    offHourText: string,
    openMarketPricing: CompanyPricing,
    offHourPricing: CompanyPricing,
    offHourPriceCutOff: number
  }>();
  public openNewNoteEditor$ = new Subject<string>();
  public openViewAlertsModal$ = new Subject<void>();
  public goToOverviewNote$ = new Subject<string>();
  public textContentPosted$ = new Subject<any>();
  /**
   * Do not use this source to call `next` outside of this class, use the exposed public `onRequestGetMilestone$`
   */
  private updateCompanyMemberSource = new Subject<null>();
  /**
   * By calling the `next` method of this subject, will request the parent component, which is subscribe to, to request the list again
   * with the params and request body set in this same service
   */
  public updateCompanyMember$ = this.updateCompanyMemberSource.asObservable();

  constructor() { }

  /**
   * Triggers Opening a new Note Editor
   */
  public openNewNoteEditor () {
    this.openNewNoteEditor$.next(undefined);
  }

  /**
   * Triggers Going to the most recent overview note
   */
  public goToOverviewNote () {
    this.goToOverviewNote$.next(undefined);
  }

  /**
   * Fired on modal open
   * @param {T} data: record from the list on edit
   */
  public openModal<T> (data?: T) {
    this.openModal$.next(data);
  }

  /**
   * Used to collapse all sections on change space
   */
  public collapseSections () {
    this.collapseSections$.next(undefined);
  }

  /**
   * Used notify subscribers on a company section opens and update counters if necessary
   * @param value: om object with the section to open and the new counter value for the section
   */
  public openCompanySection (value: {section: string, updateCounter?: boolean}) {
    this.openCompanySection$.next(value);
  }

  /**
   * Called when the text content info is saved
   */
  public textContentPosted () {
    this.textContentPosted$.next(undefined);
  }

  /**
   * Called on company section opens from a sub nav event
   * @param {boolean} section: section to open
   * @param noteId
   */
  public openNotesSection (section: boolean, noteId?: number) {
    this.openNotesSection$.next({section, noteId});
  }

  /**
   * Used to notify subscribers and update its counters
   * @param {number} updatedValue: new value to present
   */
  public updateAlertsCounter (updatedValue: number) {
    this.updateAlertsCounter$.next(updatedValue);
  }

  /**
   * Notify subscribers when the alerts list is updated
   */
  public alertRecordsUpdated () {
    this.alertRecordsUpdated$.next(undefined);
  }

  /**
   * Used to notify subscribers and update the section counter
   * @param updatedValue: new counter value to display
   */
  public updateCompanySectionCounter (updatedValue: {section: string, value: number}) {
    this.updateCompanySectionCounter$.next(updatedValue);
  }

  /**
   * On company edit modal updates company links
   */
  public companyLinksUpdated () {
    this.companyLinksUpdated$.next(undefined);
  }

  /**
   * Event to fire when company identifiers are updated
   */
  public companyIdentifiersUpdated () {
    this.companyIdentifiersUpdated$.next(undefined);
  }

  /**
   * Event to fire when company securities are updated
   */
  public companySecuritiesUpdated() {
    this.companySecuritiesUpdated$.next(undefined);
  }

  /**
   * Event to fire when company space is updated
   */
  public companySpaceUpdated () {
    this.companySpaceUpdated$.next(undefined);
  }

  /**
   * Event to fire when the selected company space is deleted
   */
  public currentCompanySpaceDeleted () {
    this.currentCompanySpaceDeleted$.next(undefined);
  }

  /**
   * Open the modal to edit company financial info
   * @param data
   */
  public openFinancialEditModal (data?: CompanyFinancial) {
    this.openFinancialEditModal$.next(data);
  }

  /**
   * Detects on company space changed to notify other components
   * @param {number} data
   */
  public spaceChanged (data: number) {
    this.spaceChanged$.next(data);
  }

  /**
   * Detects company param id changes and notify other components
   * @param data
   */
  public companyRouteParamChanged (data: {isFirstLoad: boolean, companyId: number}) {
    this.companyRouteParamChanged$.next(data);
  }

  /**
   * Emits an event to notify the text content component about auto save
   * @param data
   */
  public autoSaveDraftNotifier (data?: Note) {
    this.autoSaveDraftNotifier$.next(data);
  }

  /**
   * Emits an event to open the manual copy modal
   * @param data
   */
  public openCopyModal (data: {
    openMarketText: string,
    offHourText?: string,
  }): void {
    this.openCopyModal$.next(data);
  }

  /**
   * Emits an event to open the off hour copy modal
   * @param data
   */
  public openOffHourCopyModal (data: {
    openMarketText: string,
    offHourText: string,
    openMarketPricing: CompanyPricing,
    offHourPricing: CompanyPricing,
    offHourPriceCutOff: number
  }): void {
    this.openOffHourCopyModal$.next(data);
  }

  /**
   * Used to update company member
   */
  public updateCompanyMember () {
    this.updateCompanyMemberSource.next(undefined);
  }

  openViewAlertsModal() {
    this.openViewAlertsModal$.next();
  }
}
